
import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'signup';
  setMode: (mode: 'login' | 'signup') => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, mode, setMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<{message: string, code?: string}>({message: ''});
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const { signInWithGoogle } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setError({message: ''});
      setInfoMessage('');
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  const isLoginMode = mode === 'login';

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError({message: ''});
    try {
      await signInWithGoogle();
      onClose();
    } catch (err: any) {
      console.error('Detailed Google Sign-In Error:', err);
      
      let msg = 'Google Sign-In failed.';
      if (err.code === 'auth/popup-closed-by-user') {
        msg = 'The login popup was closed before completion.';
      } else if (err.code === 'auth/unauthorized-domain') {
        msg = `This domain (${window.location.hostname}) is not authorized in your Firebase Console.`;
      } else if (err.code === 'auth/operation-not-allowed') {
        msg = 'Google Auth is not enabled in your Firebase project settings.';
      } else {
        msg = err.message || 'An unexpected error occurred.';
      }
      
      setError({ message: msg, code: err.code });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError({message: ''});
    setInfoMessage('');
    setLoading(true);

    try {
      if (isLoginMode) {
        // Login Logic
        await auth.signInWithEmailAndPassword(email, password);
        onClose();
      } else {
        // Sign Up Logic
        const userCredential = await auth.createUserWithEmailAndPassword(email, password);
        
        if (userCredential.user) {
           // 1. Update Profile (Name)
           if (fullName) {
             try {
                await userCredential.user.updateProfile({ displayName: fullName });
             } catch (profileErr) {
                console.warn("Profile name update failed, but account created.", profileErr);
             }
           }

           // 2. Send Verification Email
           try {
             await userCredential.user.sendEmailVerification();
             setInfoMessage('Account created! A verification link has been sent to ' + email);
           } catch (emailErr: any) {
             console.error("Email verification failed:", emailErr);
             // We still close the modal because the account IS created, but we warn the user.
             // Instead of auto-closing, we might want to let them read the message, 
             // but usually showing the banner in App.tsx is better for "Resend".
             setInfoMessage('Account created, but we could not send the verification email. Please use "Resend Link" button at the top of the page.');
           }

           // Delay closing slightly so user sees the success message
           setTimeout(() => onClose(), 4000);
           return;
        }
      }
    } catch (err: any) {
      let friendlyError = err.message;
      if (err.code === 'auth/wrong-password') friendlyError = 'Incorrect password.';
      if (err.code === 'auth/user-not-found') friendlyError = 'No account found with this email.';
      if (err.code === 'auth/email-already-in-use') friendlyError = 'This email is already in use.';
      if (err.code === 'auth/weak-password') friendlyError = 'Password should be at least 6 characters.';
      setError({ message: friendlyError, code: err.code });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/80 backdrop-blur-md transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md m-4 transition-all duration-500 overflow-hidden ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'}`}>
        <div className="p-8 md:p-10 relative">
          <button onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full bg-gray-50 text-gray-400 hover:text-gray-900 transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-2">{isLoginMode ? 'Welcome Back' : 'Get Started'}</h2>
            <p className="text-gray-500 font-medium text-sm">{isLoginMode ? 'Enter your details to access your dashboard' : 'Join our ecosystem and transform your retail experience'}</p>
          </div>

          {error.message && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl animate-reveal">
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                <div className="flex-1">
                  <p className="text-xs font-bold leading-tight">{error.message}</p>
                  {error.code === 'auth/unauthorized-domain' && (
                    <div className="mt-3 pt-3 border-t border-red-100">
                      <p className="text-[10px] opacity-80 mb-2">To fix this, go to Firebase Console &gt; Auth &gt; Settings &gt; Authorized Domains and add:</p>
                      <code className="block bg-white/50 p-2 rounded-lg text-[10px] font-mono break-all">{window.location.hostname}</code>
                    </div>
                  )}
                  {error.code && <p className="mt-2 text-[9px] uppercase tracking-widest opacity-50 font-black">Error Code: {error.code}</p>}
                </div>
              </div>
            </div>
          )}

          {infoMessage && (
            <div className={`mb-6 p-4 border text-xs rounded-2xl font-bold flex items-start gap-3 animate-reveal ${infoMessage.includes('could not send') ? 'bg-amber-50 border-amber-100 text-amber-700' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
              <svg className="h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
              <span>{infoMessage}</span>
            </div>
          )}

          <div className="space-y-4">
            <button 
              onClick={handleGoogleLogin}
              disabled={googleLoading || loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border border-gray-200 rounded-2xl font-bold text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              {googleLoading ? (
                <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-800 rounded-full animate-spin"></div>
              ) : (
                <>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-gray-100"></div>
              <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-black uppercase tracking-widest">or email</span>
              <div className="flex-grow border-t border-gray-100"></div>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {!isLoginMode && (
                <div>
                  <input 
                    type="text" 
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Full Name" 
                    required={!isLoginMode}
                    className="w-full px-6 py-4 bg-gray-50 border border-transparent text-gray-900 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-900/5 focus:border-[#003B5C] outline-none transition-all font-medium" 
                  />
                </div>
              )}
              <div>
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address" 
                  required
                  className="w-full px-6 py-4 bg-gray-50 border border-transparent text-gray-900 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-900/5 focus:border-[#003B5C] outline-none transition-all font-medium" 
                />
              </div>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password" 
                  required
                  className="w-full px-6 py-4 bg-gray-50 border border-transparent text-gray-900 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-900/5 focus:border-[#003B5C] outline-none transition-all font-medium pr-14" 
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a10.05 10.05 0 014.13-4.832M8.11 8.11l3.78 3.78M9.04 9.04a2.99 2.99 0 014.04 4.04l-3.12-3.12zm10.96 10.96l-18-18" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
              
              {isLoginMode && (
                <div className="text-right px-1">
                  <button type="button" className="text-xs font-bold text-[#003B5C] hover:underline">Forgot password?</button>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading || googleLoading}
                className="w-full bg-[#003B5C] hover:bg-[#002840] text-white font-black py-4.5 rounded-2xl transition-all duration-300 shadow-xl shadow-blue-900/20 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  isLoginMode ? 'Sign In' : 'Create Account'
                )}
              </button>
            </form>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-50 text-center">
            <p className="text-gray-500 font-medium text-sm">
              {isLoginMode ? "Don't have an account?" : "Already member?"}
              <button onClick={() => setMode(isLoginMode ? 'signup' : 'login')} className="font-black text-[#003B5C] ml-2 hover:underline">
                {isLoginMode ? 'Sign Up' : 'Log In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
